import { BinaryType } from "../@types/Binary";
import { Arquitecture } from "./Arquitecture";
import * as fs from "fs";
import * as path from "path";
import * as os from "os";
import followRedirects from 'follow-redirects';
const { https } = followRedirects;
import * as unzipper from "unzipper";
import { IncomingMessage } from 'http';
import ora from "ora";
import chalk from "chalk";

interface DownloadProgress {
    downloaded: number;
    total: number;
    percentage: number;
}

class Downloader {
    private static mainSpinner: any = null;
    private static currentSpinner: any = null;

    static get(prefix: string): BinaryType & { name: string; url: string } {
        const arch = new Arquitecture();
        const platform = arch.getPlatform();
        const architecture = arch.getArchitecture();

        const platformMap: Record<string, string> = {
            windows: "windows",
            linux: "linux",
            darwin: "macos"
        };

        const archMap: Record<string, string> = {
            x86_64: "x64",
            aarch64: "arm64"
        };

        const plat = platformMap[platform];
        const archSuffix = archMap[architecture];

        if (plat && archSuffix) {
            const baseName = `${prefix}-engine-${plat}-${archSuffix}`;
            const binaryName = platform === "windows" ? `${baseName}.exe` : baseName;

            const url = `https://github.com/Dbcube/binaries/releases/download/${prefix}-engine/${prefix}-engine-latest-${plat}-${archSuffix}.zip`;
            return {
                name: binaryName,
                url,
                query_engine: binaryName,
                schema_engine: `${prefix}-engine-${plat}-${archSuffix}${platform === "windows" ? ".exe" : ""}`
            };
        }

        return {
            name: "",
            url: "",
            query_engine: "",
            schema_engine: ""
        };
    }

    static async download(targetDir?: string): Promise<void> {
        const binaries = ['schema', 'query', 'sqlite'];
        const binDir = targetDir || this.getDefaultBinDir();
        fs.mkdirSync(binDir, { recursive: true });

        // Initialize main spinner
        this.mainSpinner = ora({
            text: chalk.blue('Descargando binarios necesarios...'),
            spinner: 'dots12'
        }).start();

        // Check which binaries need to be downloaded
        const binariesToDownload = [];
        let existingCount = 0;

        for (const prefix of binaries) {
            const binaryInfo = this.get(prefix);
            if (!binaryInfo.name || !binaryInfo.url) {
                throw new Error(`Plataforma o arquitectura no soportada para ${prefix}`);
            }

            const finalBinaryPath = path.join(binDir, binaryInfo.name);
            if (fs.existsSync(finalBinaryPath)) {
                existingCount++;
                continue;
            }

            binariesToDownload.push({
                prefix,
                binaryInfo,
                tempZipPath: path.join(os.tmpdir(), `dbcube-${prefix}-${Date.now()}.zip`),
                finalBinaryPath
            });
        }

        if (binariesToDownload.length === 0) {
            this.mainSpinner.succeed(chalk.green('Todos los binarios ya están descargados'));
            return;
        }

        // Show initial progress
        this.updateMainProgress('paralelo', existingCount, binaries.length, 'downloading');

        try {
            // Download all binaries in parallel
            await Promise.all(binariesToDownload.map(async (binary, index) => {
                const maxRetries = 3;
                let attempt = 0;

                while (attempt <= maxRetries) {
                    try {
                        // Download
                        await this.downloadFileWithProgress(binary.binaryInfo.url, binary.tempZipPath, binary.prefix);

                        // Extract
                        await this.extractBinary(binary.tempZipPath, binary.finalBinaryPath, binary.prefix);

                        // Update progress
                        const completed = existingCount + index + 1;
                        this.updateMainProgress(binary.prefix, completed, binaries.length, 'completed');
                        break;

                    } catch (error: unknown) {
                        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

                        if (attempt < maxRetries && (
                            errorMessage.includes('ECONNRESET') ||
                            errorMessage.includes('timeout') ||
                            errorMessage.includes('ETIMEDOUT') ||
                            errorMessage.includes('ENOTFOUND')
                        )) {
                            attempt++;
                            this.updateMainProgress(binary.prefix, existingCount, binaries.length, 'retrying', attempt);
                            await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000)); // Random delay to avoid conflicts
                        } else {
                            throw new Error(`Error descargando ${binary.prefix}: ${errorMessage}`);
                        }
                    }
                }
            }));

            // Complete successfully
            this.mainSpinner.succeed(chalk.green('Binarios descargados correctamente'));
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
            this.mainSpinner.fail(chalk.red(`Error en descarga paralela: ${errorMessage}`));
            throw error;
        }
    }

    private static updateMainProgress(binary: string, current: number, total: number, status: string, attempt?: number) {
        const progressBar = this.createProgressBar(current, total);
        const statusEmojis = {
            downloading: '📥',
            extracting: '📦',
            completed: '✅',
            exists: '✅',
            retrying: '🔄'
        };

        const statusMessages = {
            downloading: chalk.blue('descargando'),
            extracting: chalk.yellow('extrayendo'),
            completed: chalk.green('completado'),
            exists: chalk.gray('existe'),
            retrying: chalk.yellow(`reintentando (${attempt}/${3})`)
        };

        const emoji = statusEmojis[status as keyof typeof statusEmojis] || '📥';
        const message = statusMessages[status as keyof typeof statusMessages] || status;

        this.mainSpinner.text = `${progressBar} ${emoji} ${chalk.bold(binary)} - ${message}`;
    }

    private static createProgressBar(current: number, total: number, width: number = 20): string {
        const filled = Math.round((current / total) * width);
        const empty = width - filled;
        const filledBar = chalk.green('█'.repeat(filled));
        const emptyBar = chalk.gray('░'.repeat(empty));
        const percentage = chalk.bold(`${current}/${total}`);
        return `[${filledBar}${emptyBar}] ${percentage}`;
    }

    private static downloadFileWithProgress(url: string, outputPath: string, prefix: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = https.get(url, { timeout: 0 }, (response: IncomingMessage) => {
                if (response.statusCode === 302 || response.statusCode === 301) {
                    const redirectUrl = response.headers.location;
                    if (redirectUrl) {
                        return this.downloadFileWithProgress(redirectUrl, outputPath, prefix).then(resolve).catch(reject);
                    }
                }

                if (response.statusCode !== 200) {
                    reject(new Error(`HTTP ${response.statusCode} para ${prefix}`));
                    return;
                }

                const file = fs.createWriteStream(outputPath);
                const totalBytes = parseInt(response.headers['content-length'] || '0', 10);
                let downloadedBytes = 0;

                response.on('data', (chunk) => {
                    downloadedBytes += chunk.length;
                    file.write(chunk);

                    if (totalBytes > 0) {
                        const progress: DownloadProgress = {
                            downloaded: downloadedBytes,
                            total: totalBytes,
                            percentage: (downloadedBytes / totalBytes) * 100
                        };
                        this.updateDownloadProgress(prefix, progress);
                    }
                });

                response.on('end', () => {
                    file.end();
                    resolve();
                });

                response.on('error', (err) => {
                    file.close();
                    this.cleanupFile(outputPath);
                    reject(err);
                });

                file.on('error', (err) => {
                    file.close();
                    this.cleanupFile(outputPath);
                    reject(err);
                });
            });

            request.on('error', reject);
            request.on('timeout', () => {
                request.destroy();
                reject(new Error(`Timeout descargando ${prefix}`));
            });
        });
    }

    private static updateDownloadProgress(binary: string, progress: DownloadProgress) {
        const percentage = progress.percentage.toFixed(1);
        const downloaded = (progress.downloaded / 1024 / 1024).toFixed(1);
        const total = (progress.total / 1024 / 1024).toFixed(1);

        // Create a mini progress bar for the binary
        const barWidth = 15;
        const filled = Math.round((progress.percentage / 100) * barWidth);
        const empty = barWidth - filled;
        const progressBar = chalk.green('█'.repeat(filled)) + chalk.gray('░'.repeat(empty));

        const progressText = `[${progressBar}] ${percentage}% (${downloaded}/${total}MB)`;
        this.mainSpinner.text = `📥 ${chalk.bold(binary)} - ${progressText}`;
    }

    private static extractBinary(zipPath: string, outputPath: string, prefix: string): Promise<void> {
        return new Promise((resolve, reject) => {
            let extracted = false;

            fs.createReadStream(zipPath)
                .pipe(unzipper.Parse())
                .on('entry', (entry: unzipper.Entry) => {
                    if (entry.type === 'File' && !extracted) {
                        extracted = true;
                        const writeStream = fs.createWriteStream(outputPath);

                        entry.pipe(writeStream);

                        writeStream.on('finish', () => {
                            if (process.platform !== 'win32') {
                                fs.chmodSync(outputPath, 0o755);
                            }
                            this.cleanupFile(zipPath);
                            resolve();
                        });

                        writeStream.on('error', (err: Error) => {
                            this.cleanupFile(zipPath);
                            reject(err);
                        });
                    } else {
                        entry.autodrain();
                    }
                })
                .on('error', (err: Error) => {
                    this.cleanupFile(zipPath);
                    reject(err);
                })
                .on('close', () => {
                    if (!extracted) {
                        this.cleanupFile(zipPath);
                        reject(new Error(`No se encontró archivo válido en el ZIP para ${prefix}`));
                    }
                });
        });
    }

    private static cleanupFile(filePath: string): void {
        try {
            if (fs.existsSync(filePath)) {
                fs.unlinkSync(filePath);
            }
        } catch {
            // Ignore cleanup errors
        }
    }

    private static getDefaultBinDir(): string {
        // Try to find a suitable directory for binaries in order of preference
        const possibleDirs = [
            path.resolve(process.cwd(), '.dbcube', 'bin'),
            path.resolve(process.cwd(), 'node_modules', '.dbcube', 'bin'),
            path.resolve(__dirname, '..', 'bin'),
        ];

        // Use the first one that can be created or already exists
        for (const dir of possibleDirs) {
            try {
                if (!fs.existsSync(dir)) {
                    fs.mkdirSync(dir, { recursive: true });
                }
                // Test write permissions
                const testFile = path.join(dir, '.test');
                fs.writeFileSync(testFile, 'test');
                fs.unlinkSync(testFile);
                return dir;
            } catch {
                // Try next directory
                continue;
            }
        }

        // Fallback to .dbcube directory in temp
        const tempDir = path.join(os.tmpdir(), '.dbcube', 'bin');
        fs.mkdirSync(tempDir, { recursive: true });
        return tempDir;
    }
}

export { Downloader };